import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import FinanceNav from "@/components/finance/FinanceNav";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export const chargeMoney = (minor) => `₦${((Number(minor) || 0) / 100).toLocaleString()}`;
export const newAssignmentKey = () => `assignment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export const studentPlacements = (students, sessionId) => students.flatMap((student) =>
  (student.programs || []).filter((placement) => placement.session_id === sessionId && placement.status !== "withdrawn").map((placement) => ({
    id: placement.enrollment_id,
    label: `${student.full_name} · ${student.admission_no || "No admission no."} · ${placement.program_name || "Programme"} · ${placement.class_name || "Class"}`,
  }))
);
export const assignmentPayload = ({ productId, context, targetType, targetIds, quantity, requestKey, notes }) => ({
  product_id: productId,
  session_id: context.session.id,
  term_id: context.term.id,
  target_type: targetType,
  target_ids: targetType === "all_active" ? [] : targetIds,
  quantity: Math.max(1, Number(quantity) || 1),
  request_key: requestKey,
  notes: notes || undefined,
});

export const parentOrderingPayload = (state) => ({
  enabled: Boolean(state.enabled),
  max_quantity_per_order: Math.max(1, Number(state.max_quantity_per_order) || 1),
  max_quantity_per_term: state.max_quantity_per_term === "" || state.max_quantity_per_term == null ? null : Math.max(1, Number(state.max_quantity_per_term) || 1),
  class_ids: state.class_ids || [],
  program_ids: state.program_ids || [],
  aliases: String(state.aliases || "").split(",").map((item) => item.trim()).filter(Boolean),
});

const blankProduct = {
  name: "", category: "", product_type: "service", inventory_item_id: "",
  sku: "", unit: "unit", selling_price: "", cost_price: "", chargeable: true,
  stock_tracked: false, active: true, description: "",
};

export default function FinanceChargeCatalogue() {
  const { user } = useAuth();
  const roles = new Set(user?.roles || (user?.role ? [user.role] : []));
  const canManage = ["super_admin", "director", "inventory_officer"].some((role) => roles.has(role));
  // Parent self-ordering delegates the charge decision, so it follows product-charge authority.
  const canSetParentOrdering = ["super_admin", "director", "accountant"].some((role) => roles.has(role));
  const [ordering, setOrdering] = useState(null);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [context, setContext] = useState(null);
  const [form, setForm] = useState(blankProduct);
  const [editing, setEditing] = useState(null);
  const [productId, setProductId] = useState("");
  const [targetType, setTargetType] = useState("students");
  const [targetIds, setTargetIds] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [requestKey, setRequestKey] = useState(newAssignmentKey);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [p, c, s, cls, inv] = await Promise.all([
        api.get("/finance/products"),
        api.get("/finance/context"),
        api.get("/students"),
        api.get("/classes"),
        api.get("/inventory").catch(() => ({ data: [] })),
      ]);
      setProducts(p.data); setContext(c.data); setStudents(s.data); setClasses(cls.data); setInventory(inv.data);
      setProductId((value) => value || p.data.find((item) => item.active && item.chargeable)?.id || "");
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const placements = useMemo(() => studentPlacements(students, context?.session?.id), [students, context]);
  const targetOptions = targetType === "students" ? placements : targetType === "classes"
    ? classes.filter((item) => item.is_active !== false).map((item) => ({ id: item.id, label: item.name }))
    : targetType === "programs" ? (context?.programs || []).map((item) => ({ id: item.id, label: item.name }))
    : [];

  const selectTargets = (event) => {
    setTargetIds(Array.from(event.target.selectedOptions, (option) => option.value));
    setPreview(null);
  };
  const payload = () => assignmentPayload({ productId, context, targetType, targetIds, quantity, requestKey, notes });

  const saveProduct = async () => {
    try {
      setBusy(true);
      const data = {
        name: form.name, category: form.category, sku: form.sku || null, unit: form.unit,
        selling_price_minor: Math.round(Number(form.selling_price) * 100),
        cost_price_minor: form.cost_price === "" ? null : Math.round(Number(form.cost_price) * 100),
        chargeable: form.chargeable, active: form.active, description: form.description || null,
      };
      if (editing) {
        await api.patch(`/finance/products/${editing.id}`, data);
      } else {
        await api.post("/finance/products", {
          ...data, product_type: form.product_type,
          inventory_item_id: form.product_type === "inventory" ? form.inventory_item_id || null : null,
          stock_tracked: form.product_type === "inventory" ? form.stock_tracked : false,
        });
      }
      setForm(blankProduct); setEditing(null); await load();
      toast.success(editing ? "Catalogue item updated" : "Catalogue item created");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const editProduct = (product) => {
    setEditing(product);
    setForm({
      ...blankProduct, ...product,
      inventory_item_id: product.inventory_item_id || "",
      selling_price: product.selling_price_minor / 100,
      cost_price: product.cost_price_minor == null ? "" : product.cost_price_minor / 100,
    });
  };
  const toggleActive = async (product) => {
    try {
      await api.patch(`/finance/products/${product.id}/active`, { active: !product.active });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const editOrdering = (product) => {
    const policy = product.parent_ordering || {};
    setOrdering({
      product, enabled: Boolean(policy.enabled), max_quantity_per_order: policy.max_quantity_per_order || 1,
      max_quantity_per_term: policy.max_quantity_per_term ?? "", class_ids: policy.class_ids || [],
      program_ids: policy.program_ids || [], aliases: (policy.aliases || []).join(", "),
    });
  };
  const saveOrdering = async () => {
    try {
      setBusy(true);
      await api.patch(`/finance/products/${ordering.product.id}/parent-ordering`, parentOrderingPayload(ordering));
      toast.success(ordering.enabled ? "Parents can now order this item" : "Parent ordering turned off");
      setOrdering(null); await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const previewAssignment = async () => {
    try {
      setBusy(true);
      const { data } = await api.post("/finance/charge-assignments/preview", payload());
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const confirmAssignment = async () => {
    if (!preview || !window.confirm(`Create ${preview.eligible_students} individual obligation(s) totalling ${chargeMoney(preview.expected_total_minor)}?`)) return;
    try {
      setBusy(true);
      const { data } = await api.post("/finance/charge-assignments", payload());
      toast.success(`${data.created_count} charge(s) created; ${data.existing_count} already existed`);
      setPreview(null); setTargetIds([]); setRequestKey(newAssignmentKey());
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return <div>
    <FinanceNav />
    <PageHeader overline="Finance" title="Products, Services & Charge Assignment" description="Maintain reusable approved catalogue items and assign individual canonical obligations without changing school-fee structures." />
    {error && <Card className="p-4 mb-4 text-red-700 border-red-200">{error}</Card>}

    {canManage && <Card className="p-5 mb-6 space-y-4">
      <h2 className="font-semibold text-lg">{editing ? "Edit catalogue item" : "Create catalogue item"}</h2>
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
        <div><Label>Type</Label><Select disabled={Boolean(editing)} value={form.product_type} onValueChange={(value) => setForm({ ...form, product_type: value, inventory_item_id: "", stock_tracked: value === "inventory" })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="service">Service</SelectItem><SelectItem value="inventory">Physical Item</SelectItem></SelectContent></Select></div>
        <div><Label>Selling price (₦)</Label><Input type="number" min="0.01" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} /></div>
        <div><Label>Optional cost price (₦)</Label><Input type="number" min="0" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} /></div>
        <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
        <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
        {form.product_type === "inventory" && !editing && <div><Label>Inventory item</Label><Select value={form.inventory_item_id} onValueChange={(value) => setForm({ ...form, inventory_item_id: value })}><SelectTrigger><SelectValue placeholder="Select stock item" /></SelectTrigger><SelectContent>{inventory.map((item) => <SelectItem key={item.id} value={item.id}>{item.name} · stock {item.quantity}</SelectItem>)}</SelectContent></Select></div>}
        <div className="md:col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <label><input type="checkbox" checked={form.chargeable} onChange={(e) => setForm({ ...form, chargeable: e.target.checked })} /> Chargeable</label>
        {!editing && form.product_type === "inventory" && <label><input type="checkbox" checked disabled /> Stock tracked (required)</label>}
        <label><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
      </div>
      <div className="flex gap-2"><Button disabled={busy || !form.name || !form.category || !form.selling_price || (!editing && form.product_type === "inventory" && !form.inventory_item_id)} onClick={saveProduct}>{editing ? "Save changes" : "Create item"}</Button>{editing && <Button variant="outline" onClick={() => { setEditing(null); setForm(blankProduct); }}>Cancel</Button>}</div>
    </Card>}

    <Card className="p-5 mb-6 overflow-x-auto">
      <h2 className="font-semibold text-lg mb-3">Approved catalogue</h2>
      <table className="w-full text-sm"><thead><tr className="text-left"><th className="p-2">Name</th><th>Type</th><th>Price</th><th>Status</th><th /></tr></thead><tbody>{products.map((product) => <tr className="border-t" key={product.id}><td className="p-2">{product.name}<div className="text-xs text-slate-500">{product.category}</div></td><td className="capitalize">{product.product_type === "inventory" ? "Physical Item" : "Service"}</td><td>{chargeMoney(product.selling_price_minor)}</td><td>{product.active ? "Active" : "Inactive"}{!product.chargeable ? " · Not chargeable" : ""}{product.parent_ordering?.enabled ? <div className="text-xs text-emerald-700">Parents can order</div> : null}</td><td className="space-x-2 whitespace-nowrap">{canManage && <><Button size="sm" variant="outline" onClick={() => editProduct(product)}>Edit</Button><Button size="sm" variant="outline" onClick={() => toggleActive(product)}>{product.active ? "Deactivate" : "Activate"}</Button></>}{canSetParentOrdering && <Button size="sm" variant="outline" onClick={() => editOrdering(product)}>Parent ordering</Button>}</td></tr>)}</tbody></table>
    </Card>

    {ordering && <Card className="p-5 mb-6 space-y-4" data-testid="parent-ordering-editor">
      <div><h2 className="font-semibold text-lg">Parent ordering · {ordering.product.name}</h2>
        <p className="text-sm text-slate-500">Allow parents to order this item for their children (for example through the DFA Assistant). The price always comes from this catalogue; parents confirm before any charge is created.</p></div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ordering.enabled} onChange={(e) => setOrdering({ ...ordering, enabled: e.target.checked })} /> Parents may order this item</label>
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label>Max quantity per order</Label><Input type="number" min="1" max="20" value={ordering.max_quantity_per_order} onChange={(e) => setOrdering({ ...ordering, max_quantity_per_order: e.target.value })} /></div>
        <div><Label>Max per child per term (blank = no limit)</Label><Input type="number" min="1" max="100" value={ordering.max_quantity_per_term} onChange={(e) => setOrdering({ ...ordering, max_quantity_per_term: e.target.value })} /></div>
        <div><Label>Other names parents use (comma separated)</Label><Input value={ordering.aliases} placeholder="e.g. sports wear, PE kit" onChange={(e) => setOrdering({ ...ordering, aliases: e.target.value })} /></div>
      </div>
      <div><Label>Only for these classes (none selected = all classes) — hold Ctrl/Cmd to select several</Label>
        <select multiple className="w-full min-h-32 border rounded-md p-2 text-sm" value={ordering.class_ids} onChange={(e) => setOrdering({ ...ordering, class_ids: Array.from(e.target.selectedOptions, (option) => option.value) })}>
          {classes.filter((item) => item.is_active !== false).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        {ordering.program_ids.length > 0 && <p className="text-xs text-slate-500 mt-1">Also limited to {ordering.program_ids.length} programme(s).</p>}
      </div>
      <div className="flex gap-2"><Button disabled={busy} onClick={saveOrdering}>Save parent ordering</Button><Button variant="outline" onClick={() => setOrdering(null)}>Close</Button></div>
    </Card>}

    <Card className="p-5 space-y-4">
      <h2 className="font-semibold text-lg">Assign charges</h2>
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label>Charge / service / item</Label><Select value={productId} onValueChange={(value) => { setProductId(value); setPreview(null); }}><SelectTrigger><SelectValue placeholder="Select approved item" /></SelectTrigger><SelectContent>{products.filter((item) => item.active && item.chargeable).map((item) => <SelectItem key={item.id} value={item.id}>{item.name} · {chargeMoney(item.selling_price_minor)}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Session</Label><Input disabled value={context?.session?.name || ""} /></div>
        <div><Label>Term</Label><Input disabled value={context?.term?.name || ""} /></div>
        <div><Label>Target type</Label><Select value={targetType} onValueChange={(value) => { setTargetType(value); setTargetIds([]); setPreview(null); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="students">Individual / selected students</SelectItem><SelectItem value="classes">One / multiple classes</SelectItem><SelectItem value="programs">One / multiple programmes</SelectItem><SelectItem value="all_active">All active students</SelectItem></SelectContent></Select></div>
        <div><Label>Quantity per student</Label><Input type="number" min="1" max="100" value={quantity} onChange={(e) => { setQuantity(e.target.value); setPreview(null); }} /></div>
        <div><Label>Optional notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
      </div>
      {targetType !== "all_active" && <div><Label>Targets — hold Ctrl/Cmd to select several</Label><select multiple className="w-full min-h-40 border rounded-md p-2 text-sm" value={targetIds} onChange={selectTargets}>{targetOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></div>}
      <div className="flex gap-2"><Button variant="outline" disabled={busy || !context || !productId || (targetType !== "all_active" && !targetIds.length)} onClick={previewAssignment}>Preview assignment</Button>{preview && <Button disabled={busy || preview.eligible_students === 0} onClick={confirmAssignment}>Confirm individual charges</Button>}</div>
      {preview && <div className="rounded-md border bg-slate-50 p-4 text-sm"><strong>{preview.eligible_students} eligible student(s)</strong><div>{chargeMoney(preview.unit_price_minor)} × {preview.quantity} each</div><div className="text-lg font-semibold mt-1">Expected total: {chargeMoney(preview.expected_total_minor)}</div><p className="text-xs text-slate-500 mt-2">Retries with this assignment key will return the same obligations instead of duplicating them.</p></div>}
    </Card>
  </div>;
}

